// ============================================================================
// FSA TEMPLATE PDF PREVIEW MODAL
// ============================================================================
// Fetches the server-rendered template preview PDF as a Blob, renders it
// inline in an <iframe> (browser-native PDF viewer), and offers Download
// + Open/Print. Mirrors the CRS Booking Confirmation PDF preview pattern
// (`projects/crs/crs-web/src/components/modals/modules/booking-module/BookingPdfPreviewModal.tsx`)
// because Chrome's "Save as PDF" from an HTML preview produced unopenable
// output on the FSA template due to its size and table complexity.
//
// The PDF is generated by FsaTemplatePdfService on the backend (QuestPDF),
// so the browser receives a finished binary — no HTML reflow at print
// time, no font/CSS races, and the file is identical whether downloaded
// or printed.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fsaTemplateService } from 'api/services/fsa/fsaTemplateService';
import notificationService from 'services/notificationService';

interface FsaTemplatePdfPreviewModalProps {
  isOpen: boolean;
  templateId: string | null;
  /** Used to derive the download filename (e.g. `IN_SCH3_DIV1_COMPANY`). */
  templateCode?: string | null;
  /** Used in the modal title chip. */
  templateName?: string | null;
  onClose: () => void;
}

/**
 * India FY convention: 31 March is the most recent FY end on or before today.
 * Returns ISO date string (YYYY-MM-DD) for the picker default.
 */
const defaultIndiaFyEnd = (): string => {
  const today = new Date();
  const year = today.getMonth() >= 3 /* April or later */ ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-03-31`;
};

const FsaTemplatePdfPreviewModal: React.FC<FsaTemplatePdfPreviewModalProps> = ({
  isOpen,
  templateId,
  templateCode,
  templateName,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Preview options — drive what the backend hydrator synthesises.
  // Changing either re-fetches the PDF.
  const [periodEnd, setPeriodEnd] = useState<string>(defaultIndiaFyEnd());
  const [mode, setMode] = useState<'actual' | 'rounded'>('rounded');

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (!isOpen || !templateId) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await fsaTemplateService.getPreviewPdfBlob(templateId, { periodEnd, mode });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        // The blob channel doesn't carry the JSON envelope — fall back to
        // axios's generic message.
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load preview.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, templateId, periodEnd, mode]);

  const downloadName = templateCode
    ? `FSA-Template-${templateCode}.pdf`
    : templateId
    ? `FSA-Template-${templateId}.pdf`
    : 'FSA-Template.pdf';

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    // Open the blob in a new tab. The browser renders the binary PDF via
    // its native viewer; File → Print from there produces a faithful PDF
    // (re-saving the already-rendered PDF, not re-rendering HTML).
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups from this site so the PDF can open in a new tab.',
        6000
      );
    }
  }, [blobUrl]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.print size={16} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Template Preview
              {(templateCode || templateName) && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                  · {templateCode}{templateName ? ` — ${templateName}` : ''}
                </span>
              )}
            </h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Preview controls — period-end + amount mode. Changing either
            re-fetches the PDF so the auditor sees the actual rendered captions. */}
        <div
          style={{
            display: 'flex', gap: '1rem', alignItems: 'center',
            padding: '0.5rem 1rem',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-primary)',
            flexWrap: 'wrap',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: 500 }}>Period end</span>
            <input
              type="date"
              className="form-input"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              style={{ fontSize: '0.8125rem', padding: '0.25rem 0.5rem', maxWidth: 160 }}
              disabled={loading}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
            <span style={{ fontWeight: 500 }}>Amount mode</span>
            <select
              className="form-input"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'actual' | 'rounded')}
              style={{ fontSize: '0.8125rem', padding: '0.25rem 0.5rem' }}
              disabled={loading}
            >
              <option value="rounded">Rounded</option>
              <option value="actual">Actual</option>
            </select>
          </label>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            Preview uses the same rendering pipeline as the engagement print.
          </span>
        </div>

        <div
          className="modal-content"
          style={{
            flex: 1,
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-secondary)',
          }}
        >
          {loading && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
              }}
            >
              <div
                style={{
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid var(--border-color)',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Rendering PDF…
              </span>
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '2rem',
                textAlign: 'center',
              }}
            >
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not load preview</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{error}</div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="FSA Template Preview PDF"
              style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

export default FsaTemplatePdfPreviewModal;
